import BookingInscription from '@/classes/Booking/BookingInscription.class';
import Formulaire_Empreinte_Carbone from '@/classes/Formulaire_Empreinte_Carbone/Formulaire_Empreinte_Carbone.class';
import Commande_Inscription from "@/classes/Commande/Commande_Inscription.class";
import { defineStore } from 'pinia';
import { useMainStore } from './mainStore';
import * as custom_hooks from "@/assets/js/hooks.js";
import PublicService from '@/services/public.service';
import UserService from '@/services/user.service';
import LyraService from '@/services/lyra.service';
import AdminService from '@/services/admin.service';
import moment from 'moment';


export const useBookingStore = defineStore('booking', {
    state: () => ({
        id: null,
        uuid: null,
        id_module: null,
        id_account: 0,
        prix: 0,
        prix_total: 0,
        nb_articles: 0,
        taxe: 0,
        exclude_taxe: false,
        is_groupe: false,
        nom_groupe: "",
        inscriptions: [],
        transaction: null,
        initial_commande_inscription: null,
        formulaire_empreinte_carbone: null,
        paiement_admin: null,
        vip_ins: null,
        dossard_bourse: null,
    }),
    actions: {
        /**
         * Initialise le store de réservation avec les paramètres fournis
         * @param {number} id_module - ID du module
         * @param {boolean} exclude_taxe - Indique si la taxe doit être exclue
         * @param {number} id_account - ID du compte
         * @param {boolean} is_inscription_groupe - Indique si c'est une inscription de groupe
         */
        init(id_module, exclude_taxe, id_account, is_inscription_groupe) {
            this.id_module = id_module;
            this.exclude_taxe = exclude_taxe;
            this.id_account = id_account;
            this.is_groupe = is_inscription_groupe;
        },

        /**
         * Initializes inscriptions based on the given preselection.
         * If there are already inscriptions, the function returns early.
         * Adds a default inscription if none exist.
         * If a preselection is provided, initializes inscriptions based on the course type.
         * 
         * @param {Object} preselection - The preselection object containing course details.
         */
        init_inscriptions(preselection) {

            if (this.inscriptions.length > 0) {
                return;
            }
            // On ajoute une inscription par défaut
            this.addInscription();
            // Si une préselection est passée en paramètre, on initialise les inscriptions
            if (preselection && preselection.course) {
                // Si la course est une course en équipe ou en relais, on ajoute le nombre de coureurs nécessaires
                if ((preselection.course.type == "relais" || preselection.course.type == "equipe") && preselection.course.metadatas.nb_coureurs_equipe) {
                    let nb_coureurs_to_add = preselection.course.metadatas.nb_coureurs_equipe - 1;
                    for (let i = 0; i < nb_coureurs_to_add; i++) {
                        this.addInscription();
                    }
                }
                // Si la course est une course en duo, on ajoute un coureur
                if (preselection.course.type == "duo") {
                    this.addInscription();
                }
            }
            this.commitToLocalStorage();
        },

        init_from_dossard_bourse(dossard_bourse) {
            this.dossard_bourse = dossard_bourse;
            const mainStore = useMainStore();
            this.inscriptions[0].list_courses.initialize_from_mainstore(mainStore.module.courses.list);
            this.inscriptions[0].toggle_course_selection_from_dossard_bourse(dossard_bourse);
            this.inscriptions[0].set_finished(false);
            this.inscriptions[0].list_options.initialize_from_mainstore(mainStore.module.options.list);
            this.inscriptions[0].filter_availables_options();
            this.inscriptions[0].list_options.toggle_from_dossard_bourse(dossard_bourse);
            this.inscriptions[0].list_activites.initialize_from_mainstore(mainStore.module.activites.list);
            this.inscriptions[0].list_activites.toggle_from_dossard_bourse(dossard_bourse);
            this.inscriptions[0].list_articles.initialize_from_mainstore(mainStore.module.articles_boutique.list);
            this.inscriptions[0].list_articles.toggle_from_dossard_bourse(dossard_bourse);
            const frais_livraison = mainStore.module.frais_livraison;
            this.inscriptions[0].list_articles.set_base_livraison_price(frais_livraison);
            const has_gia = mainStore.module.has_gia;
            this.inscriptions[0].initialize_gia(has_gia);
            this.inscriptions[0].initialize_frais_bourse_dossard();
            this.inscriptions[0].donation.initialize_from_mainstore(mainStore.module.donation);
            this.inscriptions[0].update_all_carts();


            this.compute_cart();


        },

        async show_payment_form() {
            const mainStore = useMainStore();
            let response = { status: false, errors: [] };
            const isBookingDossardBourse = mainStore.dossard_bourse_mode && this.dossard_bourse;
            const isBookingVip = mainStore.inscription_vip;
            const isBookingAdmin = mainStore.inscription_as_admin;
            if (isBookingDossardBourse || isBookingVip || isBookingAdmin) {
                response.status = true;
                return response;
            }
            const check_availabilities = await this.check_availabilities();
            return check_availabilities;
        },





        /**
         * Commits the booking data to local storage.
         */
        commitToLocalStorage() {
            const mainStore = useMainStore();
            const id_item = "sportips_booking_" + mainStore.module.base;
            const id_expires_item = "sportips_booking_expires_" + mainStore.module.base;
            let local_booking = {
                id: this.id,
                uuid: this.uuid,
                id_module: this.id_module,
                id_account: this.id_account,
                prix: this.prix,
                prix_total: this.prix_total,
                nb_articles: this.nb_articles,
                taxe: this.taxe,
                exclude_taxe: this.exclude_taxe,
                is_groupe: this.is_groupe,
                nom_groupe: this.nom_groupe,
                inscriptions: this.inscriptions,
                transaction: this.transaction,
                paiement_admin: this.paiement_admin,
                vip_ins: this.vip_ins,
                dossard_bourse: this.dossard_bourse
            };
            localStorage.setItem(id_item, JSON.stringify(local_booking));
            localStorage.setItem(id_expires_item, moment().add(10, 'minutes'));
        },

        /**
         * Initializes the booking data from local storage if available and not expired.
         * 
         * This function checks for the presence of booking data and its expiration time in the local storage.
         * If the data is found and is not expired, it sets the booking data to the local state.
         * If the data is expired, it removes the booking data and its expiration time from the local storage.
         */
        initializeFromLocalStorage() {
            this.clearLocalStorageFromOtherModules();
            const mainStore = useMainStore();
            const id_item = "sportips_booking_" + mainStore.module.base;
            const id_expires_item = "sportips_booking_expires_" + mainStore.module.base;
            if (localStorage.getItem(id_item)) {
                if (localStorage.getItem(id_expires_item) && moment().isBefore(localStorage.getItem(id_expires_item))) {
                    let local_booking = JSON.parse(localStorage.getItem(id_item));
                    this.setBookingFromLocalStorage(local_booking);
                }
                else {
                    localStorage.removeItem(id_item);
                    localStorage.removeItem(id_expires_item);
                }
            }
        },

        clearState() {
            this.id = null;
            this.uuid = null;
            this.id_module = null;
            this.id_account = 0;
            this.prix = 0;
            this.prix_total = 0;
            this.nb_articles = 0;
            this.taxe = 0;
            this.exclude_taxe = false;
            this.is_groupe = false;
            this.nom_groupe = "";
            this.inscriptions = [];
            this.transaction = null;
            this.initial_commande_inscription = null;
            this.paiement_admin = null;
            this.vip_ins = null;
            this.dossard_bourse = null;
        },

        clearLocalStorage() {
            const mainStore = useMainStore();
            const id_item = "sportips_booking_" + mainStore.module.base;
            const id_expires_item = "sportips_booking_expires_" + mainStore.module.base;
            localStorage.removeItem(id_item);
            localStorage.removeItem(id_expires_item);
        },

        /**
         * Clears local storage items that are not related to the current module.
         * 
         * This function iterates through all items in the local storage and removes
         * those that are related to booking but not associated with the current module.
         * 
         * The keys of the items to be removed must include either "sportips_booking_" 
         * or "sportips_booking_expires_", and must not include the base path of the 
         * current module.
         * 
         */
        clearLocalStorageFromOtherModules() {
            const mainStore = useMainStore();
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key.includes(mainStore.module.base)) {
                    continue;
                }
                if (key.includes("sportips_booking_") || key.includes("sportips_booking_expires_")) {
                    localStorage.removeItem(key);
                }
            }
        },

        /**
         * Sets the booking details from the provided local storage object.
         *
         */
        setBookingFromLocalStorage(local_booking) {
            this.id = local_booking.id;
            this.uuid = local_booking.uuid;
            this.id_module = local_booking.id_module;
            this.id_account = local_booking.id_account;
            this.prix = local_booking.prix;
            this.prix_total = local_booking.prix_total;
            this.nb_articles = local_booking.nb_articles;
            this.taxe = local_booking.taxe;
            this.exclude_taxe = local_booking.exclude_taxe;
            this.is_groupe = local_booking.is_groupe;
            this.nom_groupe = local_booking.nom_groupe;
            this.setInscriptionsFromLocalStorage(local_booking.inscriptions);
            this.transaction = local_booking.transaction;
            this.paiement_admin = local_booking.paiement_admin;
            this.vip_ins = local_booking.vip_ins;
            this.dossard_bourse = local_booking.dossard_bourse;
        },

        /**
         * Sets the inscriptions from local storage.
         * Iterates over the provided inscriptions array, creates a new Inscription
         * instance for each item, sets its data from local storage, and pushes it
         * to the inscriptions array of the current instance.
         *
         * @param {Array} inscriptions - The array of inscriptions data from local storage.
         */
        setInscriptionsFromLocalStorage(inscriptions) {

            for (let i = 0; i < inscriptions.length; i++) {

                let inscription = new BookingInscription(this.id_module);
                inscription.setInscriptionFromLocalStorage(inscriptions[i]);
                this.inscriptions.push(inscription);
            }
        },

        /**
         * Initializes the initial command inscription.
         * 
         * This asynchronous function creates a new instance of `Commande_Inscription`,
         * sets its ID, retrieves its data, computes any applicable reductions, and
         * assigns it to `this.initial_commande_inscription`.
         * 
         * @param {number} id_commande_inscription - The ID of the command inscription to initialize.
         * @returns {Promise<void>} A promise that resolves when the initialization is complete.
         */
        async init_initial_commande_inscription(id_commande_inscription) {
            const commande_inscription = new Commande_Inscription();
            commande_inscription.id = id_commande_inscription;
            await commande_inscription.get();
            commande_inscription.compute_reduction();
            this.initial_commande_inscription = commande_inscription;
        },

        /**
         * Adds a new inscription to the inscriptions array.
         * Creates a new Inscription instance with the current module ID and pushes it to the inscriptions array.
         */
        addInscription() {

            let ins = new BookingInscription(this.id_module);
            this.inscriptions.push(ins);
        },

        /**
         * Removes an inscription from the inscriptions array at the specified index.
         *
         * @param {number} index - The index of the inscription to remove.
         */
        removeInscription(index) {
            this.inscriptions.splice(index, 1);
        },

        /**
         * Checks the availabilities of a booking.
         *
         * @async
         * @returns {Promise<Object>} An object containing the status and message of the availability check.
         * @returns {boolean} return.status - The status of the availability check.
         * @returns {string[]} return.message - An array of messages, either errors or success messages.
         */
        async check_availabilities() {

            let res = await PublicService.checkCommandeAvailabilities(this.$state);
            if (!res) return { status: false, errors: ["Une erreur est survenue"] };
            return { status: res.checked, errors: res.errors };
        },

        /**
         * Handles the process of posting a free checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to true if both the post request and the paid request are successful, otherwise false.
         */
        async post_free_checkout() {
            const mainStore = useMainStore();
            this.vip_ins = mainStore?.inscription_vip?.$state;
            this.id_account = mainStore.authUser.userId;
            this.generate_uuid();
            const post_request = await this.post();
            const paid_request = await this.set_paid_free_checkout();
            return post_request && paid_request;
        },

        /**
         * Posts the admin checkout information.
         * 
         * @param {Object} paiement_admin - The payment information for the admin.
         * @returns {Promise<Object>} The response from the post_admin request.
         */
        async post_admin_checkout(paiement_admin) {
            this.paiement_admin = paiement_admin;
            this.generate_uuid();
            const post_request = await this.post_admin();
            return post_request;
        },

        /**
         * Posts the default checkout information.
         * 
         * This method retrieves necessary data from the main store, generates a UUID,
         * and sends a post request with the checkout information.
         * 
         * @async
         * @function post_default_checkout
         * @returns {Promise<Object>} The response from the post request.
         */
        async post_default_checkout() {
            const mainStore = useMainStore();
            this.vip_ins = mainStore?.inscription_vip?.$state;
            this.id_account = mainStore.authUser.userId;
            this.generate_uuid();
            const post_request = await this.post();
            return post_request;
        },

        /**
         * Handles the process of posting a free change of parcours checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to a boolean indicating 
         * whether both the post request and the paid request were successful.
         */
        async post_free_change_parcours_checkout() {
            const mainStore = useMainStore();
            this.vip_ins = mainStore?.inscription_vip?.$state;
            this.id_account = mainStore.authUser.userId;
            this.generate_uuid();
            const post_request = await this.post();
            const paid_request = await this.set_paid_free_checkout_changement_parcours();
            return post_request && paid_request;
        },

        /**
         * Posts a transferable inscription.
         *
         * This method sends a transferable inscription to the server using the UserService.
         * It retrieves the main store, sets the VIP inscription and account ID, generates a UUID,
         * and then posts the transferable inscription.
         *
         * @param {Object} transferable_inscription - The inscription data to be transferred.
         * @returns {Promise<Object>} The response from the server.
         */
        async post_transfert_inscription(transferable_inscription) {
            const mainStore = useMainStore();
            this.vip_ins = mainStore?.inscription_vip?.$state;
            this.id_account = mainStore.authUser.userId;
            this.generate_uuid();

            let res = await UserService.postTransfertInscription(this.$state, transferable_inscription);
            return res;

        },

        /**
         * Sends a POST request to create a new booking.
         * 
         * @async
         * @function post
         * @returns {Promise<Object>} The response from the server, containing the booking details.
         * @property {number} id - The ID of the created booking.
         * @property {string} uuid - The UUID of the created booking.
         */
        async post() {
            let res = await UserService.postCommande(this.$state);
            if (res && res.id && res.uuid) {
                this.id = res.id;
                this.uuid = res.uuid;
                return true;
            }
            return false;
        },

        /**
         * Sends a booking request to the admin service and updates the store with the response.
         * 
         * @async
         * @function post_admin
         * @returns {Promise<Object>} The response from the admin service, containing the booking details.
         */
        async post_admin() {
            let res = await AdminService.postCommande(this.$state);
            return res;
        },

        /**
         * Sets the paid free checkout status for the current booking.
         * 
         * This method calls the LyraService to set the paid free checkout status
         * using the current booking's UUID. If the operation is successful, it
         * updates the transaction information for the free checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to a boolean indicating
         * whether the operation was successful.
         */
        async set_paid_free_checkout() {
            let res = await LyraService.setPaidFreeCheckout(this.uuid);

            if (res) {
                this.set_transaction_infos_free_checkout();
            }
            return res;
        },

        /**
         * Sets the paid/free checkout changement parcours.
         * 
         * This function calls the LyraService to set the paid/free checkout changement parcours
         * for the current booking identified by `this.uuid`. If the operation is successful,
         * it updates the transaction information for a free checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to `true` if the operation was successful, otherwise `false`.
         */
        async set_paid_free_checkout_changement_parcours() {
            let res = await LyraService.setPaidFreeCheckoutChangementParcours(this.uuid);
            if (res) {
                this.set_transaction_infos_free_checkout();

            }
            return res;
        },

        /**
         * Checks if the current booking has already been paid.
         * 
         * This function calls the UserService to verify if the current booking
         * has already been paid. It returns a boolean indicating the payment status.
         * 
         * @returns {Promise<boolean>} A promise that resolves to true if the booking is paid, otherwise false.
         */
        async check_if_already_paid() {
            let res = await UserService.checkIfCommandeAlreadyPaid(this.$state);
            return res && res.paid;


        },

        /**
         * Sets the transaction information for a free checkout.
         * If the transaction object does not exist, it initializes it.
         * The transaction information includes an order ID and an amount of 0.
         */
        set_transaction_infos_free_checkout() {
            if (!this.transaction) this.transaction = {};
            this.transaction.commande_infos = { orderId: this.uuid, amount: 0 };
        },

        /**
         * Sets the transaction object.
         *
         * @param {Object} transaction - The transaction object to be set.
         */
        set_transaction(transaction) {
            this.transaction = transaction;
        },

        generate_uuid() {
            if (!this.uuid) {
                const timestamp = Date.now().toString().slice(0, -2);
                const firstrandomDigits = "1" + Math.floor(10 + Math.random() * 90).toString();
                const lastrandomDigits = Math.floor(10 + Math.random() * 90).toString();
                this.uuid = firstrandomDigits + timestamp + lastrandomDigits;
            }
        },



        get_nb_usage_code_reduction(id_code_reduction) {
            return this.inscriptions.filter(inscription => inscription.code_reduction && inscription.code_reduction.id == id_code_reduction).length;

        },


        /**
         * Generates a unique identifier (UUID) for the instance if it doesn't already exist.
         * The UUID is generated using a random number multiplied by the current timestamp.
         * 
         * @method
         */
        compute_cart() {
            this.generate_uuid();
            this.handle_dossard_offert_groupe();
            this.prix = 0;
            this.prix_total = 0;
            this.nb_articles = 0;
            this.taxe = 0;

            for (let i = 0; i < this.inscriptions.length; i++) {
                this.inscriptions[i].update_cart("courses", this.vip_booking);
                this.inscriptions[i].compute_cart();
                this.prix += parseFloat(this.inscriptions[i].cart.prix);
                this.nb_articles += parseInt(this.inscriptions[i].cart.nb_articles);
            }
            if (this.initial_commande_inscription) {
                this.apply_remise_initial_inscription();
            }
            this.prix = parseFloat(this.prix.toFixed(2));
            this.taxe = this.compute_taxe();
            this.taxe = parseFloat(this.taxe.toFixed(2));
            this.prix_total = this.prix;
            if (!this.exclude_taxe) {
                this.prix_total += this.taxe;
            }
            this.prix_total = parseFloat(this.prix_total.toFixed(2));

        },

        /**
         * Computes the tax based on the module ID and price.
         *
         * @returns {number} The computed tax.
         */
        compute_taxe() {
            if (this.id_module == 63) {
                return custom_hooks.compute_tax_TSV24(this.prix);
            }
            if (this.id_module && this.id_module == 229) {
                return custom_hooks.compute_tax_TSV25(this.prix);
            }

            if (this.id_module && this.id_module == 68) {
                return custom_hooks.compute_tax_CAPVAR24(this.prix);
            }
            if (this.id_module && this.id_module == 233) {
                return custom_hooks.compute_tax_CAPVAR25(this.prix);
            }

            if (this.id_module && this.id_module == 85) {
                return custom_hooks.compute_tax_TMB24(this.prix);
            }
            if (this.id_module && this.id_module == 177) {
                return custom_hooks.compute_tax_VTTCAV24(this.prix);
            }
            if (this.id_module && this.id_module == 96) {
                return custom_hooks.compute_tax_ENR24(this.$state);
            }
            if (this.id_module && this.id_module == 250) {
                return custom_hooks.compute_tax_CVRIB25(this.prix);
            }


            // Si le prix est à 0, la taxe est de 0
            if (this.prix == 0) return 0;
            // Si le prix est inférieur à 9, la taxe est de 0,5
            if (this.prix < 9) {
                return 0.5;
            }
            // Si le prix est compris entre 9 et 100, la taxe est de 7% ou 1,2€, ce qu'il y a de plus élevé
            if (this.prix >= 9 && this.prix < 100) {
                let taxe = (this.prix * 7) / 100;
                taxe = Math.max(1.2, taxe);
                return parseFloat(taxe);
            }
            // Si le prix est compris entre 100 et 200, la taxe est de 6%
            if (this.prix >= 100 && this.prix < 200) {
                let taxe = (this.prix * 6) / 100;
                return parseFloat(taxe);
            }
            // Si le prix est supérieur ou égal à 200, la taxe est de 5%
            if (this.prix >= 200) {
                let taxe = (this.prix * 5) / 100;
                return parseFloat(taxe);
            }
        },

        /**
         * Applies an initial discount to the booking price.
         * 
         * This method checks if there is an initial price for the booking and if it is greater than zero.
         * If so, it subtracts this initial price from the current price, ensuring that the resulting price
         * does not go below zero. The final price is then rounded to two decimal places.
         */
        apply_remise_initial_inscription() {
            if (this.initial_commande_inscription.prix && parseFloat(this.initial_commande_inscription.prix) > 0) {
                this.prix -= parseFloat(this.initial_commande_inscription.prix);
                this.prix = Math.max(0, this.prix);
                this.prix = parseFloat(this.prix.toFixed(2));
            }
        },



        /**
         * Handles the offering of bibs (dossards) for a group.
         * 
         * This function checks if there are any bibs to be offered for a group. If there are, it deletes any existing bib offer codes,
         * sorts the inscriptions by price, and then offers bibs to the cheapest inscriptions that qualify.
         * 
         * The number of bibs to be offered is determined by the total number of qualifying inscriptions divided by the number of bibs offered per group.
         * 
         * @returns {void}
         */
        handle_dossard_offert_groupe() {
            const mainStore = useMainStore();
            const nb_bib = mainStore.module.dossard_offert_groupe;
            if (!nb_bib) return;
            this.deleteBibOfferCode();
            if (this.inscriptions.length < nb_bib) return;
            const sortedInscriptionByPrice = this.inscriptions.slice().sort((a, b) => a.get_prix_course() - b.get_prix_course());
            const sortedInscriptionByPriceWithPrice = sortedInscriptionByPrice.filter(inscription => inscription.get_prix_course() > 0 && !inscription.code_reduction);
            const nbTotalInscriptions = this.inscriptions.filter(inscription => inscription.get_prix_course() > 0).length;
            const nbInscriptionsToOffer = Math.floor(nbTotalInscriptions / nb_bib);
            for (let i = 0; i < nbInscriptionsToOffer; i++) {
                const cheapestInscription = sortedInscriptionByPriceWithPrice[i];
                const index = this.inscriptions.findIndex(inscription => inscription == cheapestInscription);
                if (index !== -1) {
                    this.inscriptions[index].offer_bib();
                }
            }
        },

        /**
         * Validates the current booking state.
         *
         * @returns {Object} An object containing the validation status and message.
         * @returns {boolean} response.status - The validation status, true if valid, false otherwise.
         * @returns {string} response.message - The validation message providing details about the validation result.
         */
        get_validation() {
            this.compute_cart();
            this.commitToLocalStorage();
            let response = { "status": true, "message": "" };

            if (this.nb_articles == 0) {
                response.status = false;
                response.message = "Votre panier est vide, vous ne pouvez pas valider votre commande";
            }
            if (this.inscriptions.length == 0) {
                response.status = false;
                response.message = "Votre panier est vide, vous ne pouvez pas valider votre commande";
            }

            for (let i = 0; i < this.inscriptions.length; i++) {

                if (!this.inscriptions[i].coureur || !this.inscriptions[i].coureur.champs || !this.inscriptions[i].coureur.champs.mail) {
                    response.status = false;
                    response.message = "Une erreur est survenu lors de l'inscription d'un participant, vous devez renseigner un email pour chaque participant";
                    return response;
                }
                if (Object.keys(this.inscriptions[i].coureur.champs).length === 0) {
                    response.status = false;
                    response.message = "Une erreur est survenu lors de l'inscription d'un participant, veuillez vérifier les informations saisies à l'étape précedente";
                    return response;
                }
                if (this.inscriptions[i].coureur.champs.mail.value == "") {
                    response.status = false;
                    response.message = "Une erreur est survenu lors de l'inscription d'un participant, vous devez renseigner un email pour chaque participant";
                    return response;
                }
            }
            return response;
        },

        /**
         * Deletes the "DOSSARD OFFERT" code reduction from all inscriptions.
         */
        deleteBibOfferCode() {
            for (let i = 0; i < this.inscriptions.length; i++) {
                if (this.inscriptions[i].code_reduction && this.inscriptions[i].code_reduction.nom == "DOSSARD OFFERT") {
                    this.inscriptions[i].code_reduction = null;
                    this.inscriptions[i].update_cart("code_reduction");
                    this.inscriptions[i].compute_cart();
                }

            }
        },

        /**
         * Initializes the carbon footprint form by creating a new instance of the Formulaire_Empreinte_Carbone class
         * and assigning it to the formulaire_empreinte_carbone property.
         * Used for VVX25
         */
        init_formulaire_empreinte_carbone() {
            this.formulaire_empreinte_carbone = new Formulaire_Empreinte_Carbone();
        },

        getLyraCommandeInfosMetadatas() {
            let infos = {};
            infos.uuid = this.uuid;
            infos.id_account = this.id_account;
            infos.id_module = this.id_module;
            infos.prix = this.prix;
            infos.prix_total = this.prix_total;
            infos.nb_articles = this.nb_articles;
            infos.taxe = this.taxe;
            if (this.vip_ins) {
                infos.vip_ins = this.vip_ins;
            }
            if (this.paiement_admin) {
                infos.paiement_admin = this.paiement_admin;
            }
            infos.inscriptions = [];
            for (let i = 0; i < this.inscriptions.length; i++) {
                infos.inscriptions.push(this.inscriptions[i].getLyraCommandeInfosMetadatas());
            }
            return infos;
        }


    },
    getters: {

        has_inscriptions() {
            return this.inscriptions.length > 0;
        },
        has_initial_commande_inscription_loaded() {
            return this.has_inscriptions && this.initial_commande_inscription?.id_commande;
        },
        vip_booking() {
            const mainStore = useMainStore();
            if (!mainStore.inscription_vip) {
                return null;
            }
            return mainStore?.inscription_vip?.$state;
        },
        has_dossard_offert_groupe() {
            const mainStore = useMainStore();
            return mainStore.module.dossard_offert_groupe;
        },
        show_reglement_checkboxes() {
            if (!this.id_module) return false;
            if (this.inscriptions.length == 0) return false;
            let inscriptions_opened = this.inscriptions.filter((inscription) => inscription.finished == false);
            return this.nb_articles > 0 && this.prix >= 0 && inscriptions_opened.length == 0;
        },



        has_some_inscriptions_opened() {
            return this.inscriptions.filter((inscription) => inscription.finished == false).length > 0;
        },
        isInitialized() {
            return this.id_module && this.has_inscriptions;
        },

        has_remaining_credit() {
            if (!this.initial_commande_inscription) return false;
            if (this.initial_commande_inscription.prix && this.inscriptions[0].list_courses.cart) {
                if (this.inscriptions[0].list_courses.cart.nb_articles == 0) {
                    return false;
                }
                let credit = parseFloat(this.initial_commande_inscription.prix) - parseFloat(this.inscriptions[0].list_courses.cart.prix);
                credit = parseFloat(credit.toFixed(2));
                if (credit > 0) return credit;
            }
            return false;
        },




    },
});